'use client';

import React, { useState } from 'react';
import {
  BookOpen,
  ShieldCheck,
  BarChart2,
  CheckCircle2,
  Lock,
  PlayCircle,
  Clock,
  ChevronRight,
  Star,
} from 'lucide-react';
import LessonModal from './LessonModal';

type TopicKey = 'Strategy Fundamentals' | 'Risk Management' | 'Chart Patterns';
type Status = 'completed' | 'in-progress' | 'locked' | 'available';

interface Module {
  id: string;
  title: string;
  description: string;
  topic: TopicKey;
  duration: string;
  lessons: number;
  status: Status;
  progress: number; // 0-100
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  featured?: boolean;
}

const modules: Module[] = [
  // Strategy Fundamentals
  {
    id: 'sf-1',
    title: 'Trend Following Basics',
    description: 'Identify and trade with the dominant market trend using moving averages and price action.',
    topic: 'Strategy Fundamentals',
    duration: '45 min',
    lessons: 6,
    status: 'completed',
    progress: 100,
    difficulty: 'Beginner',
    featured: true,
  },
  {
    id: 'sf-2',
    title: 'Breakout & Momentum Strategies',
    description: 'Capture explosive moves by entering on confirmed breakouts with volume confirmation.',
    topic: 'Strategy Fundamentals',
    duration: '55 min',
    lessons: 7,
    status: 'completed',
    progress: 100,
    difficulty: 'Beginner',
  },
  {
    id: 'sf-3',
    title: 'Mean Reversion Setups',
    description: 'Trade pullbacks to key levels and exploit overextended price moves for high-probability entries.',
    topic: 'Strategy Fundamentals',
    duration: '60 min',
    lessons: 8,
    status: 'in-progress',
    progress: 40,
    difficulty: 'Intermediate',
  },
  {
    id: 'sf-4',
    title: 'Multi-Timeframe Analysis',
    description: 'Align entries across timeframes to increase conviction and filter low-quality setups.',
    topic: 'Strategy Fundamentals',
    duration: '70 min',
    lessons: 9,
    status: 'available',
    progress: 0,
    difficulty: 'Intermediate',
  },
  // Risk Management
  {
    id: 'rm-1',
    title: 'Position Sizing Fundamentals',
    description: 'Calculate optimal position size using the 1% rule, ATR-based stops, and account equity.',
    topic: 'Risk Management',
    duration: '40 min',
    lessons: 5,
    status: 'completed',
    progress: 100,
    difficulty: 'Beginner',
    featured: true,
  },
  {
    id: 'rm-2',
    title: 'Stop Loss Placement',
    description: 'Place stops at logical market structure levels to avoid premature exits and protect capital.',
    topic: 'Risk Management',
    duration: '50 min',
    lessons: 6,
    status: 'in-progress',
    progress: 20,
    difficulty: 'Beginner',
  },
  {
    id: 'rm-3',
    title: 'Risk-Reward Optimization',
    description: 'Build a positive expectancy edge by targeting asymmetric R:R ratios on every trade.',
    topic: 'Risk Management',
    duration: '55 min',
    lessons: 7,
    status: 'available',
    progress: 0,
    difficulty: 'Intermediate',
  },
  {
    id: 'rm-4',
    title: 'Drawdown Recovery Protocols',
    description: 'Manage losing streaks with systematic rules to protect your account and rebuild confidence.',
    topic: 'Risk Management',
    duration: '45 min',
    lessons: 6,
    status: 'locked',
    progress: 0,
    difficulty: 'Advanced',
  },
  // Chart Patterns
  {
    id: 'cp-1',
    title: 'Classic Reversal Patterns',
    description: 'Recognize head & shoulders, double tops/bottoms, and wedges to anticipate trend reversals.',
    topic: 'Chart Patterns',
    duration: '65 min',
    lessons: 8,
    status: 'completed',
    progress: 100,
    difficulty: 'Beginner',
    featured: true,
  },
  {
    id: 'cp-2',
    title: 'Continuation Patterns',
    description: 'Trade flags, pennants, and triangles as high-probability continuation setups mid-trend.',
    topic: 'Chart Patterns',
    duration: '60 min',
    lessons: 7,
    status: 'in-progress',
    progress: 60,
    difficulty: 'Intermediate',
  },
  {
    id: 'cp-3',
    title: 'Candlestick Mastery',
    description: 'Decode single and multi-candle formations to read market sentiment at key price levels.',
    topic: 'Chart Patterns',
    duration: '75 min',
    lessons: 10,
    status: 'available',
    progress: 0,
    difficulty: 'Intermediate',
  },
  {
    id: 'cp-4',
    title: 'Volume-Confirmed Patterns',
    description: 'Use volume analysis to validate pattern breakouts and separate genuine moves from fakeouts.',
    topic: 'Chart Patterns',
    duration: '55 min',
    lessons: 7,
    status: 'locked',
    progress: 0,
    difficulty: 'Advanced',
  },
];

const topicMeta: Record<TopicKey, { icon: React.ReactNode; color: string; bg: string; border: string }> = {
  'Strategy Fundamentals': {
    icon: <BookOpen size={14} />,
    color: 'text-primary',
    bg: 'bg-primary/10',
    border: 'border-primary/20',
  },
  'Risk Management': {
    icon: <ShieldCheck size={14} />,
    color: 'text-warning',
    bg: 'bg-warning/10',
    border: 'border-warning/20',
  },
  'Chart Patterns': {
    icon: <BarChart2 size={14} />,
    color: 'text-[#6366F1]',
    bg: 'bg-[#6366F1]/10',
    border: 'border-[#6366F1]/20',
  },
};

const difficultyColor: Record<string, string> = {
  Beginner: 'text-positive bg-positive-subtle',
  Intermediate: 'text-warning bg-warning-subtle',
  Advanced: 'text-negative bg-negative-subtle',
};

const statusIcon: Record<Status, React.ReactNode> = {
  completed: <CheckCircle2 size={16} className="text-primary" />,
  'in-progress': <PlayCircle size={16} className="text-warning" />,
  available: <PlayCircle size={16} className="text-muted-foreground" />,
  locked: <Lock size={14} className="text-muted-foreground" />,
};

interface ModuleCardProps {
  module: Module;
  onClick: (module: Module) => void;
}

function ModuleCard({ module, onClick }: ModuleCardProps) {
  const meta = topicMeta[module.topic];
  const isLocked = module.status === 'locked';

  return (
    <div
      onClick={() => onClick(module)}
      className={`relative bg-card border rounded-xl p-4 flex flex-col gap-3 transition-all duration-200 group ${
        isLocked
          ? 'border-border opacity-60 cursor-not-allowed' :'border-border hover:border-primary/40 hover:shadow-card-hover cursor-pointer'
      }`}
    >
      {/* Featured badge */}
      {module.featured && !isLocked && (
        <div className="absolute top-3 right-3 flex items-center gap-1 bg-primary/10 border border-primary/20 rounded-full px-2 py-0.5">
          <Star size={10} className="text-primary fill-primary" />
          <span className="text-[10px] font-semibold text-primary">Featured</span>
        </div>
      )}

      {/* Topic tag + status */}
      <div className="flex items-center justify-between">
        <div className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${meta.bg} ${meta.color} border ${meta.border}`}>
          {meta.icon}
          <span>{module.topic}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {statusIcon[module.status]}
          <span
            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${difficultyColor[module.difficulty]}`}
          >
            {module.difficulty}
          </span>
        </div>
      </div>

      {/* Title + description */}
      <div>
        <h3 className="text-sm font-bold text-foreground leading-snug mb-1 group-hover:text-primary transition-colors">
          {module.title}
        </h3>
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
          {module.description}
        </p>
      </div>

      {/* Progress bar (only when in-progress or completed) */}
      {(module.status === 'in-progress' || module.status === 'completed') && (
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground">Progress</span>
            <span className="text-[10px] font-mono-nums font-semibold text-foreground">
              {module.progress}%
            </span>
          </div>
          <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full progress-bar-inner ${
                module.status === 'completed' ? 'bg-primary' : 'bg-warning'
              }`}
              style={{ width: `${module.progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Footer: meta + CTA */}
      <div className="flex items-center justify-between mt-auto pt-1 border-t border-border">
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <Clock size={11} />
            {module.duration}
          </span>
          <span className="flex items-center gap-1">
            <BookOpen size={11} />
            {module.lessons} lessons
          </span>
        </div>
        {!isLocked && (
          <button className="flex items-center gap-1 text-[11px] font-semibold text-primary hover:text-primary/80 transition-colors">
            {module.status === 'completed' ? 'Review' : module.status === 'in-progress' ? 'Continue' : 'Start'}
            <ChevronRight size={12} />
          </button>
        )}
      </div>
    </div>
  );
}

interface ModuleGridProps {
  activeTopic: string;
}

export default function ModuleGrid({ activeTopic }: ModuleGridProps) {
  const [selectedModule, setSelectedModule] = useState<Module | null>(null);

  const filtered =
    activeTopic === 'All' ? modules : modules.filter((m) => m.topic === activeTopic);

  // Group by topic when showing all
  const grouped: Record<string, Module[]> =
    activeTopic === 'All'
      ? {
          'Strategy Fundamentals': filtered.filter((m) => m.topic === 'Strategy Fundamentals'),
          'Risk Management': filtered.filter((m) => m.topic === 'Risk Management'),
          'Chart Patterns': filtered.filter((m) => m.topic === 'Chart Patterns'),
        }
      : { [activeTopic]: filtered };

  return (
    <>
      <div className="space-y-8">
        {Object.entries(grouped).map(([topic, mods]) => {
          const meta = topicMeta[topic as TopicKey];
          const completedCount = mods.filter((m) => m.status === 'completed').length;
          return (
            <div key={topic}>
              {/* Section header */}
              <div className="flex items-center gap-3 mb-4">
                <div className={`flex items-center gap-2 ${meta.color}`}>
                  {meta.icon}
                  <h2 className="text-sm font-bold text-foreground">{topic}</h2>
                </div>
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground font-mono-nums">
                  {completedCount}/{mods.length} done
                </span>
              </div>

              {/* Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                {mods.map((mod) => (
                  <ModuleCard key={mod.id} module={mod} onClick={setSelectedModule} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <LessonModal module={selectedModule} onClose={() => setSelectedModule(null)} />
    </>
  );
}
